import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useRequete } from '../api/client';
import type { ApercuRadars, EcartRadar } from '../api/types';
import {
  Anneau,
  BadgeSignal,
  Chargement,
  Chiffre,
  Message,
} from '../composants/elements';

/**
 * Écart radar / compétences — §6.3.1. La lecture que le radar seul ne donne
 * pas : une techno en *adopt* sans expert est un risque de livraison signé.
 */
export function EcartRadarPage(): JSX.Element {
  const [radar, setRadar] = useState<string>('');
  const [signal, setSignal] = useState<string>('tous');

  const apercu = useRequete<ApercuRadars>('/api/radars');
  const ecart = useRequete<EcartRadar>(
    `/api/dashboards/ecart-radar${radar ? `?radar=${encodeURIComponent(radar)}` : ''}`,
    [radar],
  );

  if (ecart.erreur) {
    return (
      <div className="contenu section">
        <Message variante="erreur">{ecart.erreur.message}</Message>
      </div>
    );
  }

  if (!ecart.donnees) return <Chargement quoi="Computing the gap" />;

  const lignes = ecart.donnees.lignes.filter((l) => signal === 'tous' || l.signal === signal);

  return (
    <div className="contenu">
      <div className="entete-page">
        <div>
          <p className="eyebrow" style={{ marginBottom: 14 }}>
            Management · {ecart.donnees.trimestre}
          </p>
          <h1 style={{ fontSize: 'clamp(28px, 3.4vw, 44px)' }}>Radar gap vs. skills</h1>
          <p className="plomb" style={{ marginTop: 16, maxWidth: 620 }}>
            Coverage = people at 3 or above. Advanced = 4 or above. Reference = 5. The lines that
            matter rise to the top on their own.
          </p>
        </div>

        <div className="chiffres">
          <Chiffre petit valeur={ecart.donnees.resume.risques} libelle="Risks" />
          <Chiffre petit valeur={ecart.donnees.resume.dettes} libelle="Debts" />
          <Chiffre
            petit
            valeur={ecart.donnees.lignes.filter((l) => l.signal === 'inconnu').length}
            libelle="No data"
          />
          <Chiffre petit valeur={ecart.donnees.resume.repondants} libelle="Profiles filled in" />
        </div>
      </div>

      <div className="barre-actions" style={{ paddingBottom: 20 }}>
        <select
          className="selection"
          style={{ width: 'auto' }}
          value={radar}
          onChange={(e) => setRadar(e.target.value)}
          aria-label="Filter by radar"
        >
          <option value="">All radars</option>
          {(apercu.donnees?.radars ?? []).map((r) => (
            <option key={r.radar} value={r.radar}>
              {r.libelle}
            </option>
          ))}
        </select>

        <select
          className="selection"
          style={{ width: 'auto' }}
          value={signal}
          onChange={(e) => setSignal(e.target.value)}
          aria-label="Filter by signal"
        >
          <option value="tous">All signals</option>
          <option value="risque">Risks only</option>
          <option value="dette">Debts only</option>
          <option value="inconnu">No data — nobody answered</option>
          <option value="ok">No signal</option>
        </select>

        <span className="discret">{lignes.length} technolog{lignes.length === 1 ? 'y' : 'ies'}</span>
      </div>

      <div className="tableau-defilant" style={{ paddingBottom: 'var(--space-9)' }}>
        <table className="tableau">
          <thead>
            <tr>
              <th>Technology</th>
              <th>Ring</th>
              <th>Group</th>
              <th className="tableau__nombre">Coverage</th>
              <th className="tableau__nombre">Advanced</th>
              <th className="tableau__nombre">Reference</th>
              <th className="tableau__nombre">Respondents</th>
              <th>Signal</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => (
              <tr key={l.technologieId}>
                <td style={{ fontWeight: 500 }}>{l.titre}</td>
                <td>
                  <Anneau anneau={l.anneau} />
                </td>
                <td className="discret">{l.libelleQuadrant}</td>
                <td className="tableau__nombre">{l.couverture}</td>
                <td className="tableau__nombre">{l.experts}</td>
                <td className="tableau__nombre">{l.referents}</td>
                <td className="tableau__nombre discret">{l.repondants}</td>
                <td>
                  <BadgeSignal signal={l.signal} />
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Link className="discret" to={`/qui-connait?techno=${encodeURIComponent(l.titre)}`}>
                    Who knows →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
